import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { useSmoothCounter } from '@/hooks/useSmoothCounter';
import { useEnforcement } from '@/hooks/useEnforcement';
import { handleUpgrade, getCurrency } from '@/lib/checkout';
import {
  defaultBillingCycle,
  formatPlanPrice,
  isCapTier,
  lookupForCycle,
  planDisplayName,
} from '@/lib/plans';
import { useSubscription } from '@/hooks/useSubscription';
import { numberLocale } from '@/lib/language';
import { trackConversion } from '@/lib/analytics';
import { supabase } from '@/integrations/supabase/client';
import { avgPostScore, fetchRunProductIds, RUN_MEASURABLE_MAX_PRODUCTS } from '@/lib/runScore';
import { BeforeAfterDiff } from '@/components/shared/BeforeAfterDiff';
import type { OptimizationJob } from '@/types/optimization';

interface Props {
  feedId: string | null;
  job: OptimizationJob | null;
  preScore: number | null;
  phaseTimings: { sortieren?: number; texte?: number; attribute?: number; scoring?: number };
  // C-19: der Klick traegt die GEMESSENEN Vorher/Nachher-Werte dieses Laufs
  // weiter, damit die Werkbank ihre Subline mit dem Erfolgsmoment fuellen
  // kann. `ctx` ist optional, weil aeltere Aufrufer (Tests, Dev-Preview) ohne
  // Kontext weiter funktionieren muessen; `pre`/`post` sind einzeln nullable,
  // weil beide Zahlen an dieser Stelle fehlen KOENNEN (kein Vorher-Score,
  // Score-Job noch nicht fertig) — nie erfunden, nie mit 0 aufgefuellt.
  onReviewWorkbench: (ctx?: { pre: number | null; post: number | null }) => void;
}

/**
 * Wie lange nach Job-Abschluss der Score-Poller auf FRISCHE Zeilen wartet,
 * bevor er ehrlich aufgibt (`scoreLater` statt "wird berechnet …").
 *
 * Seit dem Frische-Praedikat in `lib/runScore.ts` liefert `avgPostScore` bei
 * stale-only-Zeilen `null` — richtig so, aber damit kann der Poller in den
 * Faellen, in denen NIE ein Score-Job entsteht (Score-Trigger-Rate-Limit trifft
 * `finalizeJob` mit 429; Score-Job endet `failed`), endlos weiterpollen. Eine
 * Ansage, die nie eintrifft, war genau die Tarnung, unter der die leere
 * `product_ids`-Spalte ueberlebt hat — deshalb hat das Warten ein Ende.
 *
 * Anker ist `completed_at` des Jobs, nicht der Mount-Zeitpunkt: wer das
 * Erfolgsbild eines aelteren Laufs oeffnet, bekommt sofort die ehrliche
 * Antwort statt zwei weiterer Minuten Spinner. 2 Minuten decken den Normalfall
 * grosszuegig — Score-Jobs laufen in Sekunden (Kommentar am
 * SCORE_JOB_WAIT_TIMEOUT_MS des Modals), nur der Enqueue kann nachhinken.
 */
export const SCORE_WAIT_MAX_MS = 120_000;

/**
 * TASK 11: der "Direkt zu Google"-Knopf ist weg, nicht nur versteckt.
 *
 * Er stand hier gleichrangig neben "In der Werkbank pruefen" und war damit der
 * zweite, kuerzere Weg an der Pflicht-Freigabe vorbei — genau im Moment des
 * groessten Schwungs. Seit `enqueue-upload-batches` fuer jeden Browser-Aufruf
 * `approval_status='approved'` verlangt, haette er ausserdem nur noch die
 * Fehlermeldung "No uploadable products in selection (manual uploads require
 * approved products)" erzeugt: frisch optimierte Produkte stehen auf
 * `pending_review`. Ein Knopf, der zuverlaessig scheitert, ist schlimmer als
 * keiner.
 */
export function SuccessScoreboard({ feedId, job, preScore, onReviewWorkbench }: Props) {
  const { t, i18n } = useTranslation('optimization');
  const [exampleOpen, setExampleOpen] = useState(false);
  const [exampleIdx, setExampleIdx] = useState(0);
  const [upgradeLoading, setUpgradeLoading] = useState(false);

  // C-6: the success moment is the emotional peak — surface a light upgrade
  // nudge here when the account holds more products than the current plan covers,
  // instead of only mentioning it in the (easy to dismiss) banner elsewhere.
  //
  // UI-15: dies ist die Zahl "Produkte, die der Tarif NICHT abdeckt", nicht
  // "Produkte, die noch auf Optimierung warten". Der alte Name
  // `remainingUnoptimized` behauptete das Zweite und die Copy schrieb es aus;
  // in Produktion stand deshalb bei 3.763 Produkten, 14 davon optimiert, und
  // Limit 2.000 die Zahl 1.763 unter dem Satz "warten auf ihre Optimierung" --
  // unoptimiert waren tatsaechlich 3.749. Die Zahl bleibt (das Gate unten ist
  // eindeutig Upsell-Rahmung), Name und Copy sagen jetzt dasselbe wie sie.
  //
  // Bezugsgroesse ist bewusst kontoweit, nicht Feed-weit: `totalProducts` kommt
  // aus `useTotalProducts`, das `products` ohne Feed-Filter zaehlt (RLS scoped
  // auf den Besitzer). `limit` ist die Produkt-KAPAZITAET des Tarifs, also wie
  // viele Produkte er gleichzeitig betreut -- seit dem Bestandsmass kein
  // Lebenszeit-Deckel mehr, der einmal Verbrauchtes fuer immer festhaelt. Fuer
  // diese Zeile aendert das nichts an der Rechnung, wohl aber an ihrer Aussage:
  // die Luecke schliesst sich auch, wenn Produkte den Feed verlassen. Beide
  // Groessen sind kontoweit, der Vergleich ist also konsistent -- die Copy darf
  // nur nicht "in deinem Feed" behaupten (tat die englische Fassung).
  const { limit, totalProducts, plan, recommendedPlan, nextPlan } = useEnforcement();
  const { subscription } = useSubscription();
  const effectivePlan = recommendedPlan || nextPlan;
  const productsBeyondPlan = Math.max(0, totalProducts - limit);
  const showUpsellRow = totalProducts > limit && productsBeyondPlan > 0 && !!effectivePlan;
  // CRO 21.08.2026: Neuabschluesse checken mit Jahresabrechnung aus,
  // Bestandskunden behalten ihr Intervall. Nur fuer Baender (Legacy-Tarife
  // sind nicht mehr kaufbar; recommended/next liefern ohnehin nur Baender).
  const upsellCycle = effectivePlan && isCapTier(effectivePlan.tier)
    ? defaultBillingCycle(subscription)
    : 'monthly';

  const handleUpsellClick = async () => {
    if (!effectivePlan) return;
    trackConversion('upgrade_success_modal_clicked', { plan, target: effectivePlan.tier });
    setUpgradeLoading(true);
    try {
      const res = await handleUpgrade(lookupForCycle(effectivePlan, upsellCycle), getCurrency());
      // Abgebrochene B2B-Bestaetigung (oder kein Redirect): Spinner loesen.
      if (!res.redirected) setUpgradeLoading(false);
    } catch (error) {
      Sentry.captureException(error, { tags: { feature: 'billing', action: 'upgrade-from-success' } });
      setUpgradeLoading(false);
    }
  };

  // Der Ring misst DIESEN Lauf, nicht den Feed. `preScore` ist der Schnitt
  // genau der Produkte dieses Jobs (enqueue-optimization-batches snapshottet
  // `avg_latest_total_score(...)`), also muss die Nachher-Seite dieselbe
  // Grundmenge nehmen — sonst subtrahiert das Delta zwei verschiedene
  // Produktmengen. Der fruehere feed-weite Select war zusaetzlich bei 1000
  // Zeilen gekappt (PostgREST-Default) und zeigte auf grossen Feeds den
  // Schnitt einer unbestimmten Teilmenge.
  //
  // Welle 3 hat dafuer `job.product_ids` genommen — eine Spalte, die BEIDE
  // Erzeuger leer schreiben (129 von 129 Jobs seit 2026-05). Der Ring stand
  // deshalb bei jedem Nutzer dauerhaft auf "—" mit "Score wird berechnet …",
  // und wegen `enabled` wurde nicht einmal eine Abfrage gestellt. Die
  // Grundmenge kommt jetzt aus `optimization_events` dieses `job_id`;
  // Begruendung, Seitenlogik und Obergrenze stehen in `lib/runScore.ts`.
  //
  // Zwei getrennte Abfragen mit Absicht: die Produktmenge eines
  // abgeschlossenen Laufs aendert sich nicht mehr (einmal lesen, `staleTime:
  // Infinity`), der Score dagegen trudelt nach. Waeren beide in einer Abfrage,
  // wuerde jeder Poll die Ereignis-Seiten erneut lesen.
  const { data: runIds } = useQuery({
    queryKey: ['progress-modal-run-product-ids', feedId, job?.id],
    queryFn: () => fetchRunProductIds(job!.id),
    enabled: !!job?.id,
    staleTime: Infinity,
    // `unavailable` heisst "eine Seite hat nicht geantwortet" und ist damit
    // transient — ohne diesen Versuch waere der Ring nach einem einzigen
    // Netzfehler bis zum Neuaufbau des Fensters tot, und zwar mit demselben
    // Platzhalter, hinter dem sich die leere Spalte versteckt hat. `ids` und
    // `too-large` sind endgueltig, dort wird nicht weiter gepollt.
    refetchInterval: (query) => (query.state.data?.kind === 'unavailable' ? 2500 : false),
  });
  const runProductIds = runIds?.kind === 'ids' ? runIds.ids : [];
  const runTooLarge = runIds?.kind === 'too-large';

  // Der Cutoff des Frische-Praedikats: nur Scores, die NACH dem Abschluss
  // dieses Laufs berechnet wurden, sind sein Ertrag (lib/runScore.ts). Ohne
  // `completed_at` (defensiv — finalizeJob setzt es immer) laeuft die
  // Score-Abfrage gar nicht und die Wartefrist ankert am Mount.
  const completedAt = job?.completed_at ?? null;
  const [mountedAtMs] = useState(() => Date.now());
  const scoreWaitAnchorMs = completedAt ? new Date(completedAt).getTime() : mountedAtMs;

  // Ehrliches Ende des Wartens: nach SCORE_WAIT_MAX_MS ohne frischen Score
  // stoppt der Poller und die Copy wechselt von "wird berechnet" zu
  // scoreLater. State + Timer statt reiner Zeitrechnung, weil der Wechsel
  // MITTEN im offenen Erfolgsbild passieren kann und dafuer ein Re-Render
  // braucht.
  const [scoreWaitExpired, setScoreWaitExpired] = useState(false);
  useEffect(() => {
    const remaining = scoreWaitAnchorMs + SCORE_WAIT_MAX_MS - Date.now();
    if (remaining <= 0) {
      setScoreWaitExpired(true);
      return;
    }
    setScoreWaitExpired(false);
    const timer = window.setTimeout(() => setScoreWaitExpired(true), remaining);
    return () => window.clearTimeout(timer);
  }, [scoreWaitAnchorMs]);

  // Ein Poll kostet einen Request je 200 Produkte. Bei einem 200er-Lauf ist das
  // einer, bei 10.000 sind es 50 — deshalb haengt der Abstand an der
  // Laufgroesse und nicht an einer festen Zahl. Die Funktion wird bei JEDEM
  // Poll neu ausgewertet (kein am Mount eingefrorener Wert, Muster
  // panelRunScope).
  const scorePollMs = runProductIds.length > 1000 ? 10000 : 2500;
  const { data: scoreData } = useQuery({
    queryKey: ['progress-modal-success-score', feedId, job?.id, runProductIds.length],
    queryFn: async () => {
      const avg = await avgPostScore(runProductIds, completedAt!);
      // `null` heisst "noch nicht belastbar" (keine Zeile geschrieben, nur
      // VERALTETE Zeilen aus einem frueheren feed-weiten Score-Job, ODER ein
      // Block mit Fehler): der Platzhalter bleibt stehen und der Poller
      // versucht es wieder. Ein echter Schnitt von 0 ist dagegen ein Wert und
      // wird als "0" gezeigt.
      return { avg: avg === null ? null : Math.round(avg) };
    },
    enabled: runProductIds.length > 0 && !!completedAt,
    // Nach Ablauf der Wartefrist wird nicht weiter gepollt: bei stale-only
    // haelt das Frische-Praedikat den Wert dauerhaft auf null, und ein Poller
    // ohne Ende wuerde im Rate-Limit-Fall (kein Score-Job entstanden) ewig
    // laufen.
    refetchInterval: (query) =>
      query.state.data?.avg === null && !scoreWaitExpired ? scorePollMs : false,
  });

  // Das Beispiel stammt aus DIESEM Lauf, nicht aus dem Feed.
  //
  // Vorher las die Query die 20 hoechstgescorten `post_optimization`-Zeilen des
  // FEEDS (`.eq('feed_id', feedId)`, kein Bezug zum Job) und zog dazu den
  // aktuellen Produktstand. Im Erfolgsfenster eines Laufs stand damit auf einem
  // laenger genutzten Feed zuverlaessig das Paradestueck eines FRUEHEREN Laufs
  // — dieselbe Wurzel wie beim gestrichenen Attribut-Panel und beim "Gerade
  // fertig" aus Phase 2. Welle 3 hat den Ring dieser Datei auf `product_ids`
  // umgebaut; diese zweite Query blieb dabei liegen.
  //
  // Quelle ist jetzt `optimization_events` gefiltert auf `job_id`: der
  // Lauf-Bezug ist konstruktiv da (Index `idx_optimization_events_job_id`), es
  // ist EIN Request unabhaengig von der Laufgroesse, und `old_title`/`new_title`
  // sind das Vorher/Nachher dieses Laufs statt eines Produktstands, den ein
  // spaeterer Sync oder Lauf inzwischen verschoben haben kann. Die Scores
  // kommen im zweiten Request fuer genau diese hoechstens 40 Produkt-IDs — die
  // `.in()`-Liste ist damit gedeckelt, ohne den Lauf zu chunken.
  //
  // WAS DAS NICHT IST: die besten Beispiele des Laufs. Kandidaten sind die 40
  // JUENGSTEN Ereignisse, danach wird innerhalb dieser 40 nach Score sortiert —
  // bei einem 10.000er-Lauf also die letzten 0,4 Prozent. Ueber den ganzen Lauf
  // zu ranken hiesse, alle Ereignisse zu lesen und mit ihren Scores zu
  // verbinden, und das ist genau die Kappungs-Kaskade, gegen die die
  // Lauf-Grenze in `lib/runScore.ts` steht. Deshalb sagt die Copy jetzt
  // "Beispiele aus diesem Lauf" und nicht mehr "die besten" (Vorgabe: die
  // Beschriftung nennt, was die Zahl wirklich ist).
  const EXAMPLE_CANDIDATES = 40;
  const MIN_EXAMPLE_SCORE = 70;
  const { data: examples } = useQuery({
    queryKey: ['progress-modal-success-examples', job?.id],
    queryFn: async () => {
      if (!job?.id) return [];
      const { data: events } = await supabase
        .from('optimization_events')
        .select('product_id, old_title, new_title')
        .eq('job_id', job.id)
        .not('new_title', 'is', null)
        .order('optimized_at', { ascending: false })
        .limit(EXAMPLE_CANDIDATES);
      const candidates = (events ?? []).filter(
        (e): e is { product_id: string; old_title: string | null; new_title: string } =>
          !!e.product_id && !!e.new_title && e.new_title.length >= 20,
      );
      if (candidates.length === 0) return [];
      const { data: scored } = await supabase
        .from('product_scores')
        .select('product_id, total_score')
        .eq('score_type', 'post_optimization')
        .gte('total_score', MIN_EXAMPLE_SCORE)
        .in('product_id', candidates.map((c) => c.product_id));
      const scoreById = new Map<string, number>(
        (scored ?? [])
          .filter((s) => !!s.product_id && s.total_score != null)
          .map((s) => [s.product_id as string, Number(s.total_score)]),
      );
      return candidates
        .filter((c) => scoreById.has(c.product_id))
        .map((c) => ({
          id: c.product_id,
          title: c.old_title,
          optimized_title: c.new_title,
          quality_score: Math.round(scoreById.get(c.product_id) as number),
        }))
        .sort((a, b) => b.quality_score - a.quality_score)
        .slice(0, 5);
    },
    enabled: exampleOpen && !!job?.id,
  });

  const avgScore = scoreData?.avg ?? null;
  const avgScoreSmooth = useSmoothCounter(avgScore ?? 0);
  const delta = preScore !== null && avgScore !== null ? Math.round(avgScore - preScore) : null;

  // "optimiert" zaehlt die tatsaechlich optimierten Produkte. Ein Job mit
  // Teil-Fehlschlaegen wird trotzdem 'completed' (job-lifecycle), also stand
  // hier vorher `total_products` — neben einer Fehler-Kachel, die dieselben
  // Produkte noch einmal auswies.
  const completedCount = job?.completed_products ?? 0;
  const failed = job?.failed_products ?? 0;
  const elapsedSec = job?.created_at && job?.completed_at
    ? Math.round((new Date(job.completed_at).getTime() - new Date(job.created_at).getTime()) / 1000)
    : 0;
  const minutesPart = Math.floor(elapsedSec / 60);
  const secondsPart = elapsedSec % 60;
  // Die Annahme hinter "gespart" (rund 1 Minute Handarbeit je Produkt) steht
  // als Fussnote unter den Kacheln — eine gerechnete Zahl neben zwei
  // gemessenen braucht ihre Bezugsgroesse. Unter einer Stunde in Minuten,
  // sonst stuende im Erfolgsmoment "~0h gespart".
  const savedMinutes = Math.max(0, Math.round((completedCount * 60 - elapsedSec) / 60));
  const savedHours = Math.round(savedMinutes / 60);

  const example = examples?.[exampleIdx];

  const ringCircumference = 2 * Math.PI * 54;
  const ringOffset = ringCircumference * (1 - avgScoreSmooth / 100);

  return (
    <div className="space-y-4">
      <div className="text-center pt-2">
        <div className="text-[10px] tracking-wider text-muted-foreground mb-1">{t('progressModal.success.avgScoreCaption')}</div>
        <div className="relative w-[124px] h-[124px] mx-auto mb-2">
          <svg width="124" height="124" viewBox="0 0 124 124" style={{ transform: 'rotate(-90deg)' }}>
            <circle cx="62" cy="62" r="54" stroke="hsl(var(--muted))" strokeWidth="10" fill="none" />
            <circle
              cx="62" cy="62" r="54"
              stroke="hsl(var(--primary))" strokeWidth="10" fill="none" strokeLinecap="round"
              strokeDasharray={ringCircumference}
              strokeDashoffset={ringOffset}
              style={{ transition: 'stroke-dashoffset 1.2s ease-out' }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="text-4xl font-extrabold leading-none text-primary">
              {avgScore === null ? '—' : Math.round(avgScoreSmooth)}
            </div>
            <div className="text-[10px] text-muted-foreground tracking-wider mt-0.5">/ 100</div>
          </div>
        </div>
        {avgScore === null ? (
          // Drei verschiedene Wartezustaende, drei verschiedene Saetze. "Score
          // wird berechnet …" war fuer jeden Lauf die Antwort, auch fuer die,
          // bei denen hier nie etwas berechnet wird — genau die Dauer-Ansage,
          // die der leeren Spalte ihre Tarnung gab. `scoreLater` ist das
          // ehrliche Ende nach SCORE_WAIT_MAX_MS: der Poller steht dann still.
          <div className="text-[11px] text-muted-foreground italic">
            {runTooLarge
              ? t('progressModal.success.scoreRunTooLarge', {
                  max: RUN_MEASURABLE_MAX_PRODUCTS.toLocaleString(numberLocale(i18n.language)),
                })
              : scoreWaitExpired
                ? t('progressModal.success.scoreLater')
                : t('progressModal.success.scoreCalculating')}
          </div>
        ) : (
          // Vorgabe 2.7: negative und unveraenderte Deltas werden gezeigt,
          // nicht verschwiegen — dasselbe Muster wie scoreDeltaInkClass im
          // FeedCheckHeader und ScoreLiftBar. Ein Erfolgsbild, das nur
          // Aufwaertspfeile kennt, ist ein Hype-Wrapper.
          delta !== null && (
            <div
              className={`inline-block text-xs font-bold px-2.5 py-1 rounded-full ${
                delta > 0
                  ? 'bg-success-light text-success-ink'
                  : delta < 0
                    ? 'bg-destructive/15 text-destructive-ink'
                    : 'bg-muted text-muted-foreground'
              }`}
            >
              {delta > 0
                ? t('progressModal.success.delta', { delta, prev: Math.round(preScore!) })
                : delta < 0
                  ? t('progressModal.success.deltaDown', { delta, prev: Math.round(preScore!) })
                  : t('progressModal.success.deltaUnchanged', { prev: Math.round(preScore!) })}
            </div>
          )
        )}
      </div>

      <div className={`grid gap-2 ${failed > 0 ? 'grid-cols-4' : 'grid-cols-3'}`}>
        <Tile value={String(completedCount)} label={t('progressModal.success.tile.optimized')} />
        <Tile value={`${minutesPart}:${String(secondsPart).padStart(2, '0')}`} label={t('progressModal.success.tile.min')} />
        <Tile
          value={savedMinutes >= 60 ? `~${savedHours}h` : `~${savedMinutes} ${t('progressModal.success.tile.min')}`}
          label={t('progressModal.success.tile.saved')}
        />
        {failed > 0 && (
          <Tile value={`${failed}`} label={t('progressModal.success.tile.errors')} warning />
        )}
      </div>
      <p className="text-center text-[10px] text-muted-foreground">
        {t('progressModal.success.savedBasis')}
      </p>

      {showUpsellRow && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-dashed border-primary/30 bg-accent/30 px-3.5 py-2.5">
          <p className="text-xs text-muted-foreground leading-snug">
            {/* Preisumbau: die Zeile beziffert das Ziel-Band samt Preis —
                {{limit}}/{{remaining}} bleiben (upsellCopy.test.ts pinnt sie,
                ohne beide ist die Zahl nicht nachrechenbar). Der Jahres-Zweig
                sagt /Jahr (voller Jahresbetrag, CRO 21.08.). */}
            {t(upsellCycle === 'yearly'
              ? 'progressModal.success.upsell.lineYearly'
              : 'progressModal.success.upsell.line', {
              remaining: productsBeyondPlan.toLocaleString(numberLocale(i18n.language)),
              limit: limit.toLocaleString(numberLocale(i18n.language)),
              plan: effectivePlan ? planDisplayName(effectivePlan, i18n?.language, t) : '',
              price: effectivePlan ? formatPlanPrice(effectivePlan, getCurrency(), upsellCycle) : '',
            })}
          </p>
          <button
            type="button"
            onClick={handleUpsellClick}
            disabled={upgradeLoading}
            className="shrink-0 px-3 py-1.5 rounded-md text-xs font-bold text-primary-ink border border-primary/40 hover:bg-accent transition-colors disabled:opacity-50"
          >
            {t('progressModal.success.upsell.cta')}
          </button>
        </div>
      )}

      <button
        onClick={() => setExampleOpen(o => !o)}
        className={`w-full px-3.5 py-2.5 rounded-lg flex justify-between items-center text-xs font-semibold transition-colors ${
          exampleOpen
            ? 'border border-primary bg-accent text-primary-ink'
            : 'border border-dashed border-primary/30 bg-card text-primary-ink hover:border-primary/50 hover:bg-accent/50'
        }`}
      >
        <span>{exampleOpen ? t('progressModal.success.example.expanded') : t('progressModal.success.example.collapsed')}</span>
        <span className={`transition-transform ${exampleOpen ? 'rotate-180' : ''}`}>▾</span>
      </button>
      {exampleOpen && (
        examples === undefined ? (
          <div className="bg-muted rounded-lg border border-border px-3.5 py-6 text-center text-xs text-muted-foreground">
            {t('progressModal.success.example.loading')}
          </div>
        ) : !example ? (
          <div className="bg-muted rounded-lg border border-border px-3.5 py-6 text-center text-xs text-muted-foreground">
            {t('progressModal.success.example.empty')}
          </div>
        ) : (
          <div className="bg-muted rounded-lg border border-border overflow-hidden">
            <div className="bg-card px-3.5 py-2.5">
              <div className="flex justify-end mb-1.5">
                <div className="bg-success text-success-foreground text-[9px] font-extrabold px-1.5 py-0.5 rounded tracking-tight">
                  {t('progressModal.success.example.scoreBadge', { score: example.quality_score })}
                </div>
              </div>
              {/* sideLabels: ohne VORHER/NACHHER-Captions war im Beispiel
                  nicht erkennbar, welcher Titel der alte und welcher der
                  neue ist. */}
              <BeforeAfterDiff before={example.title} after={example.optimized_title} variant="stacked" sideLabels />
            </div>
            <div className="bg-card py-2 flex justify-center items-center gap-1.5">
              <button onClick={() => setExampleIdx(i => Math.max(0, i - 1))} className="text-muted-foreground text-xs px-2">‹</button>
              {(examples ?? []).map((_, i) => (
                <div
                  key={i}
                  className={`rounded transition-all ${
                    i === exampleIdx ? 'w-4 h-1.5 bg-primary' : 'w-1.5 h-1.5 bg-muted'
                  }`}
                />
              ))}
              <button onClick={() => setExampleIdx(i => Math.min((examples?.length ?? 1) - 1, i + 1))} className="text-muted-foreground text-xs px-2">›</button>
            </div>
          </div>
        )
      )}

      {/* Genau ein Primaer-CTA: der Weg in die Werkbank. */}
      <div className="grid grid-cols-1 gap-2.5 pt-1">
        <button
          onClick={() => onReviewWorkbench({ pre: preScore, post: avgScore })}
          className="px-4 py-3 rounded-lg font-bold text-sm text-primary-foreground bg-primary shadow-md"
        >
          {t('progressModal.success.cta.review')}
        </button>
      </div>
      <p className="text-center text-[11px] text-muted-foreground">
        {t('progressModal.success.cta.reviewHint')}
      </p>
    </div>
  );
}

function Tile({ value, label, warning }: { value: string; label: string; warning?: boolean }) {
  return (
    <div className={`rounded-lg p-2.5 text-center ${warning ? 'bg-warning-light' : 'bg-muted'}`}>
      <div className={`text-lg font-extrabold leading-none mb-1 ${warning ? 'text-warning-ink' : 'text-foreground'}`}>{value}</div>
      <div className="text-[9px] text-muted-foreground tracking-tight uppercase">{label}</div>
    </div>
  );
}
